// src/contexts/ThemeContext.jsx - Fixed with localStorage persistence
import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    
    // Fall back to system preference
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    
    return false;
  });

  // Initialize RTL from localStorage or default to false
  const [isRTL, setIsRTL] = useState(() => {
    const savedDirection = localStorage.getItem('direction');
    return savedDirection === 'rtl';
  });

  // Apply theme to document and save to localStorage
  useEffect(() => {
    const root = document.documentElement;
    
    if (isDarkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Apply RTL direction to document and save to localStorage
  useEffect(() => {
    const root = document.documentElement;
    
    if (isRTL) {
      root.setAttribute('dir', 'rtl');
      root.style.direction = 'rtl';
      localStorage.setItem('direction', 'rtl');
    } else {
      root.setAttribute('dir', 'ltr');
      root.style.direction = 'ltr';
      localStorage.setItem('direction', 'ltr');
    }
  }, [isRTL]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only update if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        setIsDarkMode(e.matches);
      }
    };

    // Use addListener for compatibility with older browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // For older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const toggleRTL = () => {
    setIsRTL(prev => !prev);
  };

  const setTheme = (theme) => {
    setIsDarkMode(theme === 'dark');
  };

  const setDirection = (direction) => {
    setIsRTL(direction === 'rtl');
  };

  const value = {
    isDarkMode,
    isRTL,
    toggleDarkMode,
    toggleRTL,
    setTheme,
    setDirection
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;